import { createSelector } from "@reduxjs/toolkit";

import { ACTIVITY_TYPES } from "Constants/constants";

import type { Activity } from "Api/activity/types";
import type { Log } from "Reducers/log";
import type { RootState } from "Store/configureStore";

/** Task-type prefixes whose activities carry an agent report. Add one line per new report-emitting agent. */
export const AGENT_REPORT_TASK_PREFIXES = [
  "performance-agent:",
  "troubleshooting-agent:"
] as const;

export const UPSUN_REPORT_KEY = "upsun_report";

export const REPORT_KIND = Object.freeze({
  SUCCESS: "success",
  ERROR: "error"
} as const);

/** The report body is markdown by default; any other value falls back to raw text rendering. */
export const REPORT_FORMAT_MARKDOWN = "markdown";

export type AgentReportResult =
  | { kind: typeof REPORT_KIND.SUCCESS; format: string; content: string }
  | { kind: typeof REPORT_KIND.ERROR; error: string };

/** The text the Report tab's Copy/Download actions operate on (report body, or error message). */
export const agentReportCopyText = (
  report: AgentReportResult | undefined
): string => {
  switch (report?.kind) {
    case REPORT_KIND.SUCCESS:
      return report.content;
    case REPORT_KIND.ERROR:
      return report.error;
    default:
      return "";
  }
};

/**
 * The Report tab applies to agent task activities. `payload.task.type` is the task TYPE (e.g.
 * `performance-agent:profile`), distinct from `parameters.task` (the task NAME) used elsewhere. The
 * match is a prefix check — the suffix identifies the variant and is not hardcoded.
 */
export const isAgentReportTaskActivity = (activity?: Activity): boolean =>
  activity?.type === ACTIVITY_TYPES.ENVIRONMENT.TASK &&
  AGENT_REPORT_TASK_PREFIXES.some(prefix =>
    (activity.payload?.task?.type ?? "").startsWith(prefix)
  );

/**
 * The pendo slug for a report's analytics, derived from the task image type: the base before the first
 * `:`, minus a trailing `-agent`, plus `-report`. Yields `performance-report`, `troubleshooting-report`,
 * `log-analyzer-report`. Callers pass a gated activity, so the empty/no-colon input is not reachable in
 * the render path.
 */
export const agentReportAnalyticsSlug = (
  taskType: string | undefined
): string => {
  const base = (taskType ?? "").split(":")[0];
  const name = base.endsWith("-agent") ? base.slice(0, -"-agent".length) : base;
  return `${name}-report`;
};

/** Non-task activities (pushes, source operations, …) carry commits; the Commits tab applies to them.
 * Shared by both the inline ActivityTabs and the LogModal so the two shells can't drift as activity
 * types are added. */
export const isNonTaskActivity = (activity?: Activity): boolean =>
  activity?.type !== ACTIVITY_TYPES.ENVIRONMENT.TASK;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalize = (
  value: Record<string, unknown>
): AgentReportResult | undefined => {
  // error wins over content when both are present (a failed run takes precedence).
  if (typeof value.error === "string" && value.error.length > 0) {
    return { kind: REPORT_KIND.ERROR, error: value.error };
  }
  if (typeof value.content === "string" && value.content.length > 0) {
    const format =
      typeof value.format === "string" ? value.format : REPORT_FORMAT_MARKDOWN;
    return { kind: REPORT_KIND.SUCCESS, format, content: value.content };
  }
  return undefined;
};

/**
 * The agent emits the report as a single JSON line on stdout, flattened into the activity log
 * (NDJSON, no stream channel). Detection: parse each entry's `message`, keep the last object
 * carrying the `upsun_report` key that interprets to a valid report, then return it. Non-JSON lines
 * (including the CLI options-echo noise line) fail to parse and are skipped; a structurally-present
 * but empty final `upsun_report` won't clobber a valid earlier one (last-valid wins).
 */
const extractReport = (log: Log[]): AgentReportResult | undefined => {
  let result: AgentReportResult | undefined;
  for (const entry of log) {
    const message = entry?.data?.message;
    if (typeof message !== "string") continue;
    // The report line carries the key verbatim; skip the JSON.parse throw/catch on plain stdout.
    if (!message.includes(UPSUN_REPORT_KEY)) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(message);
    } catch {
      continue;
    }
    if (!isObject(parsed)) continue;
    const value = parsed[UPSUN_REPORT_KEY];
    if (!isObject(value)) continue;
    const normalized = normalize(value);
    if (normalized) result = normalized;
  }
  return result;
};

// Input selectors return reference-stable values (a boolean and the log array) so a new `activity`
// identity on a poll tick doesn't invalidate the cache and re-parse an unchanged log.
const selectIsAgentReportTask = (_: RootState, activity?: Activity) =>
  isAgentReportTaskActivity(activity);

const selectActivityLog = (state: RootState, activity?: Activity) =>
  activity ? state.log[activity.id]?.log : undefined;

/**
 * Per-instance selector factory. A shared selector keyed by activityId would thrash reselect's
 * size-1 cache across the activity list, re-parsing the log on every render; each consuming
 * component memoizes its own instance instead. Out-of-scope activities short-circuit before the
 * parse runs.
 */
export const makeSelectAgentReport = () =>
  createSelector(
    selectIsAgentReportTask,
    selectActivityLog,
    (isAgentReportTask, log): AgentReportResult | undefined => {
      if (!isAgentReportTask) return undefined;
      if (!log || log.length === 0) return undefined;
      return extractReport(log);
    }
  );

/**
 * The report parsed from one activity's log, by ID rather than by activity object — for callers that
 * hold an id and no activity, i.e. the troubleshooting-run listener deciding whether a stream batch
 * completed a report. Unmemoized on purpose: a listener runs once per batch, not once per render, so
 * reselect's size-1 cache would only add a stale-read hazard. No task-type gate either — the caller
 * started the run and already knows it is an agent's.
 */
export const selectAgentReportByActivityId = (
  state: RootState,
  activityId: string
): AgentReportResult | undefined =>
  extractReport(state.log[activityId]?.log ?? []);
